import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { ArrowLeft, Lock, CheckCircle2, Loader2, Eye, EyeOff, AlertCircle } from "lucide-react";
import { toast } from "sonner";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [pw1, setPw1] = useState("");
  const [pw2, setPw2] = useState("");
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (!token) toast.error("Missing reset token. Please use the link from your email.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!token) return;
    if (pw1.length < 6) return toast.error("Password must be at least 6 characters.");
    if (pw1 !== pw2) return toast.error("Passwords don't match.");
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw1 });
      setDone(true);
      setTimeout(() => navigate("/login"), 2500);
    } catch (err) {
      const detail = err?.response?.data?.detail || "Reset failed. The link may be expired or already used.";
      toast.error(detail);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream-50 flex items-center justify-center px-4">
      <div className="w-full max-w-md">
        <div className="mb-6">
          <Link to="/login" data-testid="back-to-login"
            className="inline-flex items-center gap-1 text-sm text-ink-600 hover:text-brand font-semibold">
            <ArrowLeft size={14} /> Back to sign in
          </Link>
        </div>

        <div className="bg-white rounded-3xl shadow-card p-8 border border-border">
          {done ? (
            <div className="text-center" data-testid="reset-success">
              <CheckCircle2 size={48} className="mx-auto text-emerald-500 mb-3" />
              <h2 className="font-heading text-2xl font-extrabold text-ink">Password updated</h2>
              <p className="text-sm text-ink-600 mt-2">You can now sign in with your new password.</p>
              <Link to="/login" className="mt-6 inline-flex items-center gap-1 text-sm font-semibold text-brand hover:underline">
                Go to sign in <ArrowLeft size={14} className="rotate-180" />
              </Link>
            </div>
          ) : !token ? (
            <div className="text-center">
              <AlertCircle size={48} className="mx-auto text-rose-500 mb-3" />
              <h2 className="font-heading text-2xl font-extrabold text-ink">Invalid reset link</h2>
              <p className="text-sm text-ink-600 mt-2">Please request a new password reset link.</p>
              <Link to="/forgot-password" className="mt-4 inline-block px-5 py-2.5 rounded-full bg-brand text-white text-sm font-semibold">
                Request a new link
              </Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 mb-1">
                <div className="p-2 rounded-full bg-brand/10 text-brand"><Lock size={20} /></div>
                <h1 className="font-heading text-2xl font-extrabold text-ink">Set a new password</h1>
              </div>
              <p className="text-sm text-ink-600 mt-2">Pick a strong password — at least 6 characters.</p>
              <form onSubmit={submit} className="mt-6 space-y-4">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">New password</label>
                  <div className="relative mt-1">
                    <input data-testid="reset-pw1" type={show ? "text" : "password"} required autoFocus minLength={6}
                      value={pw1} onChange={(e) => setPw1(e.target.value)}
                      className="w-full rounded-xl border border-border bg-white px-4 py-3 pr-10 text-ink placeholder:text-ink-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                      placeholder="••••••••" />
                    <button type="button" onClick={() => setShow(s => !s)}
                      className="absolute right-3 top-1/2 -translate-y-1/2 p-1 text-ink-500 hover:text-ink">
                      {show ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-ink-500">Confirm new password</label>
                  <input data-testid="reset-pw2" type={show ? "text" : "password"} required minLength={6}
                    value={pw2} onChange={(e) => setPw2(e.target.value)}
                    className="mt-1 w-full rounded-xl border border-border bg-white px-4 py-3 text-ink placeholder:text-ink-400 focus:border-brand focus:outline-none focus:ring-2 focus:ring-brand/20"
                    placeholder="••••••••" />
                </div>
                <button data-testid="reset-submit" disabled={loading || !pw1 || !pw2}
                  className="w-full inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-brand text-white font-semibold hover:bg-brand-600 active:scale-95 disabled:opacity-60">
                  {loading ? <Loader2 size={16} className="animate-spin" /> : <Lock size={16} />}
                  Update password
                </button>
              </form>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
